import React, { useCallback, useState, useEffect, useMemo } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { sendMessage } from "../services/websocketManager";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";

import "react-awesome-button/dist/styles.css";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import SaveAltIcon from "@mui/icons-material/SaveAlt";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import KeyIcon from "@mui/icons-material/Key";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TourIcon from "@mui/icons-material/Tour";
import { useLocation } from "react-router-dom";
import { GENERAL } from "../general/General";
import { 
  Lightbulb, 
  Star, 
  AutoAwesome, 
  Groups, 
  School,
  Info,
  Launch,
} from '@mui/icons-material';
import {
  Card,
  CardContent,
  Fab,
  Slide,
  Zoom,
  Fade,
  Avatar,
  alpha,
  Grid,
  Grow,
  Container,
  Tooltip,
} from "@mui/material";

// Import tour hook
import { useWelcomeTour } from './useWelcomeTour';

const WelcomePage = ({ userData, updateBackground }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const location = useLocation();

  // Enhanced tour hook usage - pass translation function exactly like MainPage
  const welcomeTourHook = useWelcomeTour(t); // Pass the translation function
  
  // Debug what the hook returns
  console.log('🎓 Welcome tour hook returned:', welcomeTourHook);
  
  const { 
    startWelcomeTour, 
    forceShowWelcomeTour, 
    shouldShowTour, 
    resetTour,
    hasAutoStarted 
  } = welcomeTourHook || {};

  // State management
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [autoTourTriggered, setAutoTourTriggered] = useState(false);

  const friendId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("friend");
  }, [location.search]);

  // Enhanced tour conditions - exactly like MainPage
  const tourConditions = useMemo(() => ({
    canShowTour: !isDialogOpen && logoLoaded && showFeatures,
    shouldShow: typeof shouldShowTour === 'function' ? shouldShowTour() : false,
    hasTriggered: autoTourTriggered || hasAutoStarted
  }), [isDialogOpen, logoLoaded, showFeatures, shouldShowTour, autoTourTriggered, hasAutoStarted]);

  // Enhanced auto-start tour - exactly like MainPage
  useEffect(() => {
    if (tourConditions.canShowTour && tourConditions.shouldShow && !tourConditions.hasTriggered) {
      const timer = setTimeout(() => {
        setAutoTourTriggered(true);
        startWelcomeTour();
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [tourConditions, startWelcomeTour]);

  // Reset auto-trigger when conditions change - exactly like MainPage
  useEffect(() => {
    if (!logoLoaded || isDialogOpen || !showFeatures) {
      setAutoTourTriggered(false);
    }
  }, [logoLoaded, isDialogOpen, showFeatures]);

  // Animation states
  useEffect(() => {
    const timer = setTimeout(() => {
      setLogoLoaded(true);
      setTimeout(() => setShowFeatures(true), 500);
      setTimeout(() => setShowStats(true), 1000);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // Feature cycling effect
  useEffect(() => {
    if (showFeatures) {
      const interval = setInterval(() => {
        setActiveFeature(prev => (prev + 1) % 4);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [showFeatures]);

  // Handle friend joining
  useEffect(() => {
    if (friendId && userData) {
      if (friendId !== userData?.id) {
        const generalMessage = { ...generalMessageTemplate };
        generalMessage.messageType = "friend_joined";
        generalMessage.user.id = friendId;
        sendMessage(generalMessage);
      }
    }
  }, [friendId, navigate, userData, userData?.id]);

  useEffect(() => {
    document.body.style.overflow = "auto";
    updateBackground({ image: GENERAL.BACKGROUND });
    
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [updateBackground]);

  useEffect(() => {
    if (userData) {
      console.log(userData);
    }
  }, [userData]);

  // Handlers
  const handleLogin = () => navigate("/login");
  const handlePlayAsGuest = () => navigate("/main");
  const handleOpenWhyDialog = () => setIsDialogOpen(true);
  const handleCloseWhyDialog = () => setIsDialogOpen(false);

  // Enhanced manual tour handler - exactly like MainPage
  const handleManualTour = useCallback(() => {
    console.log('🎓 Manual tour clicked - available functions:', {
      startWelcomeTour: typeof startWelcomeTour,
      forceShowWelcomeTour: typeof forceShowWelcomeTour,
      shouldShowTour: typeof shouldShowTour
    });
    
    setAutoTourTriggered(true);
    
    // Try to call the function with error handling
    try {
      if (typeof forceShowWelcomeTour === 'function') {
        forceShowWelcomeTour();
      } else {
        console.error('forceShowWelcomeTour is not a function, type:', typeof forceShowWelcomeTour);
        // Fallback to regular start
        if (typeof startWelcomeTour === 'function') {
          startWelcomeTour();
        }
      }
    } catch (error) {
      console.error('Error calling tour function:', error);
    }
  }, [forceShowWelcomeTour, startWelcomeTour, shouldShowTour]);

  const handleLearnMore = () => {
    let from = "web";

    if (window.Telegram && window.Telegram.WebApp) {
      from = "telegram";
    } else {
      const ua = navigator.userAgent.toLowerCase();
      const referrer = document.referrer.toLowerCase();

      if (ua.includes("fb") || referrer.includes("facebook.com")) {
        from = "facebook";
      } else if (ua.includes("instagram") || referrer.includes("instagram.com")) {
        from = "instagram";
      } else if (ua.includes("tiktok") || referrer.includes("tiktok.com")) {
        from = "tiktok";
      }
    }

    const url = `https://landing.democraticchess.com?from=${from}`;
    window.location.href = url;
  };

  // Feature action handlers
  const handleFeatureAction = (index) => {
    switch (index) {
      case 0: // Democratic Chess - Navigate to game
        navigate("/main");
        break;
      case 1: // Coin Mining - Show info dialog
        setIsDialogOpen(true);
        break;
      case 2: // Skill Development - Navigate to learn section
        navigate("/learn");
        break;
      case 3: // Play with Friends - Navigate to team builder
        navigate("/team-builder");
        break;
      default:
        break;
    }
  };

  // Updated Feature data with action buttons
  const features = [
    {
      icon: <AutoAwesome sx={{ fontSize: 40, color: '#FFD700' }} />,
      title: t("Democratic Chess"),
      description: t("The revolutionary way to play chess where strategy meets collaboration"),
      color: '#FFD700',
      actionIcon: <PlayArrowIcon sx={{ fontSize: 18 }} />,
      actionColor: '#4CAF50'
    },
    {
      icon: <MonetizationOnIcon sx={{ fontSize: 40, color: '#4CAF50' }} />,
      title: t("Coin Mining"),
      description: t("Earn your chess coins through gameplay and strategic decisions"),
      color: '#4CAF50',
      actionIcon: <Info sx={{ fontSize: 18 }} />,
      actionColor: '#2196F3'
    },
    {
      icon: <School sx={{ fontSize: 40, color: '#2196F3' }} />,
      title: t("Skill Development"),
      description: t("Improve your chess skills through team-based learning"),
      color: '#2196F3',
      actionIcon: <Launch sx={{ fontSize: 18 }} />,
      actionColor: '#FF9800'
    },
    {
      icon: <Groups sx={{ fontSize: 40, color: '#FF6B6B' }} />,
      title: t("Play with Friends"),
      description: t("Invite friends and enjoy chess together in real-time"),
      color: '#FF6B6B',
      actionIcon: <Groups sx={{ fontSize: 18 }} />,
      actionColor: '#9C27B0'
    }
  ];

  const winRate = userData?.userStatistics?.totalGames > 0 ? 
    Math.round((userData?.userStatistics?.totalWins / userData?.userStatistics?.totalGames) * 10000) : 0;

  // Real user stats connected to actual data like Profile component
  const userStats = [
    { 
      label: t("Games Played"), 
      value: userData?.userStatistics?.totalGames || userData?.userStatistics?.gamesPlayed || 0, 
      icon: <PlayArrowIcon />,
      color: '#2196F3'
    },
    { 
      label: t("Wins"), 
      value: userData?.userStatistics?.totalWins || userData?.userStatistics?.wins || 0, 
      icon: <EmojiEventsIcon />,
      color: '#4CAF50'
    },
    { 
      label: t("Rating"), 
      value: winRate, // Convert to float like 0.12
      icon: <Star />,
      color: '#FFD700',
    },
    { 
      label: t("Coins"), 
      value: userData?.userStatistics?.coins || 0, 
      icon: <MonetizationOnIcon />,
      color: '#FF9800'
    },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background Particles */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(45deg, rgba(74, 112, 125, 0.1), rgba(33, 150, 243, 0.1))',
          zIndex: 0,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='m0 40l40-40h-40v40z'/%3E%3C/g%3E%3C/svg%3E")`,
            animation: 'float 20s ease-in-out infinite',
          },
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-20px)' },
          }
        }}
      />

      {/* Enhanced Tour Control with session-based behavior - following MainPage pattern */}
      {userData?.socialUser && !isDialogOpen && (
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 20, sm: 30 },
            left: { xs: 20, sm: 30 },
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Tooltip title={t("Show Welcome Tutorial")} placement="right">
            <Fab
              data-tour="tour-button"
              size={isMobile ? "small" : "medium"}
              onClick={handleManualTour}
              sx={{
                background: 'linear-gradient(45deg, #4a707d, #2196F3)',
                color: 'white',
                boxShadow: '0 4px 20px rgba(74, 112, 125, 0.4)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: '0 6px 25px rgba(74, 112, 125, 0.6)',
                },
                // Add pulse animation for new users
                animation: !hasAutoStarted && (typeof shouldShowTour === 'function' ? shouldShowTour() : false) ? 'pulse 2s infinite' : 'none',
                '@keyframes pulse': {
                  '0%': {
                    boxShadow: '0 4px 20px rgba(74, 112, 125, 0.4)',
                  },
                  '50%': {
                    boxShadow: '0 4px 30px rgba(74, 112, 125, 0.8)',
                    transform: 'scale(1.05)',
                  },
                  '100%': {
                    boxShadow: '0 4px 20px rgba(74, 112, 125, 0.4)',
                  },
                },
              }}
            >
              <TourIcon />
            </Fab>
          </Tooltip>
        </Box>
      )}

      <Container maxWidth="lg" sx={{ flex: 1, display: 'flex', flexDirection: 'column', zIndex: 1 }}>
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: { xs: 4, sm: 6, md: 8 },
            pb: { xs: 2, sm: 4 },
          }}
        >
          {/* Animated Logo */}
          <Zoom in={logoLoaded} timeout={800}>
            <Box
              data-tour="welcome-logo"
              sx={{
                position: 'relative',
                width: { xs: 200, sm: 280, md: 350 },
                height: { xs: 200, sm: 280, md: 350 },
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                background: 'linear-gradient(45deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
                border: '2px solid rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url(https://res.cloudinary.com/dnlgtek2a/image/upload/v1743426566/Pictures/logo.jpg)`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderRadius: 'inherit',
                  zIndex: 1,
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(45deg, rgba(74, 112, 125, 0.1), rgba(33, 150, 243, 0.1))',
                  zIndex: 2,
                  borderRadius: 'inherit',
                }
              }}
            />
          </Zoom>

          {/* Title only - removed subtitle */}
          <Fade in={logoLoaded} timeout={1200}>
            <Box sx={{ textAlign: 'center', mt: { xs: 3, sm: 4 }, px: 2 }}>
              <Typography
                variant={isMobile ? "h5" : isTablet ? "h4" : "h3"}
                sx={{
                  color: 'white',
                  fontWeight: 'bold',
                  textShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
                  mb: 2,
                  background: 'linear-gradient(45deg, #FFD700, #FFA726)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t("Democratic Chess")}
              </Typography>
            </Box>
          </Fade>
        </Box>

        {/* Features Section */}
        <Slide direction="up" in={showFeatures} timeout={800}>
          <Grid 
            container 
            spacing={2} 
            sx={{ mb: { xs: 3, sm: 4 }, px: { xs: 1, sm: 2 } }}
            data-tour="features-section"
          >
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Grow in={showFeatures} timeout={1000 + index * 200}>
                  <Box sx={{ position: 'relative', height: '100%' }}>
                    {/* Action Button - top-left */}
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFeatureAction(index);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        zIndex: 15,
                        background: `linear-gradient(45deg, ${feature.actionColor}, ${alpha(feature.actionColor, 0.8)})`,
                        color: 'white',
                        width: 32,
                        height: 32,
                        boxShadow: `0 4px 12px ${alpha(feature.actionColor, 0.4)}`,
                        '&:hover': {
                          background: `linear-gradient(45deg, ${alpha(feature.actionColor, 0.8)}, ${feature.actionColor})`,
                          transform: 'scale(1.1)',
                        },
                      }}
                    >
                      {feature.actionIcon}
                    </IconButton>

                    <Card
                      sx={{
                        height: '100%',
                        background: activeFeature === index 
                          ? `linear-gradient(135deg, ${alpha(feature.color, 0.2)}, ${alpha(feature.color, 0.1)})`
                          : 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))',
                        backdropFilter: 'blur(10px)',
                        border: `2px solid ${activeFeature === index ? feature.color : 'rgba(255,255,255,0.2)'}`,
                        borderRadius: 3,
                        transition: 'all 0.5s ease',
                        cursor: 'pointer',
                        transform: activeFeature === index ? 'scale(1.05)' : 'scale(1)',
                        boxShadow: activeFeature === index 
                          ? `0 10px 30px ${alpha(feature.color, 0.3)}`
                          : '0 4px 20px rgba(0,0,0,0.1)',
                        '&:hover': {
                          transform: 'scale(1.08)',
                          boxShadow: `0 15px 40px ${alpha(feature.color, 0.4)}`,
                        }
                      }}
                      onClick={() => setActiveFeature(index)}
                    >
                      <CardContent sx={{ textAlign: 'center', p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                          {feature.icon}
                        </Box>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: 'white', 
                            fontWeight: 'bold',
                            mb: 1,
                            fontSize: { xs: '1rem', sm: '1.1rem' }
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'rgba(255,255,255,0.8)',
                            fontSize: { xs: '0.8rem', sm: '0.9rem' }
                          }}
                        >
                          {feature.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Slide>

        {/* User Stats (for logged in users) */}
        {userData?.socialUser && showStats && (
          <Fade in={showStats} timeout={1000}>
            <Box 
              sx={{ position: 'relative', mb: { xs: 3, sm: 4 }, mx: { xs: 1, sm: 2 } }}
              data-tour="user-stats"
            >
              <Card
                sx={{
                  background: 'linear-gradient(135deg, rgba(74, 112, 125, 0.2), rgba(33, 150, 243, 0.2))',
                  backdropFilter: 'blur(15px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 3,
                }}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ color: 'white', textAlign: 'center', mb: 2, fontWeight: 'bold' }}>
                    {t("Your Progress")}
                  </Typography>
                  <Grid container spacing={2}>
                    {userStats.map((stat, index) => (
                      <Grid item xs={6} sm={3} key={index}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Avatar
                            sx={{
                              mx: 'auto',
                              mb: 1,
                              background: stat.color ? `linear-gradient(45deg, ${stat.color}, ${alpha(stat.color, 0.8)})` : 'linear-gradient(45deg, #FFD700, #FFA726)',
                              width: { xs: 40, sm: 50 },
                              height: { xs: 40, sm: 50 },
                            }}
                          >
                            {stat.icon}
                          </Avatar>
                          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                            {stat.isFloat ? stat.value.toFixed(2) : stat.value.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            {stat.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          </Fade>
        )}

        {/* Action Buttons Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: 2, sm: 3 },
            mb: { xs: 3, sm: 4 },
            px: { xs: 2, sm: 0 },
          }}
        >
          {userData?.socialUser ? (
            <Zoom in timeout={1200}>
              <Box data-tour="start-game" sx={{ position: 'relative' }}>
                <Card
                  sx={{
                    background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 10px 30px rgba(76, 175, 80, 0.4)',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: '0 15px 40px rgba(76, 175, 80, 0.6)',
                    },
                  }}
                  onClick={handlePlayAsGuest}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
                      <PlayArrowIcon sx={{ fontSize: { xs: 28, sm: 35 }, color: 'white' }} />
                      <Typography
                        variant={isMobile ? "h6" : "h5"}
                        fontWeight="bold"
                        sx={{ color: 'white', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}
                      >
                        {t("Start your game")}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            </Zoom>
          ) : (
            <Zoom in timeout={1200}>
              <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems="center">
                <Box sx={{ position: 'relative' }}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, #2196F3, #1976D2)',
                      borderRadius: 3,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 8px 25px rgba(33, 150, 243, 0.4)',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 12px 35px rgba(33, 150, 243, 0.6)',
                      },
                    }}
                    onClick={handleLogin}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <KeyIcon sx={{ fontSize: 24, color: 'white' }} />
                        <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>
                          {t("Sign up / Login")}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>

                <Box sx={{ position: 'relative' }}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, #FF6B6B, #FF5252)',
                      borderRadius: 3,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 8px 25px rgba(255, 107, 107, 0.4)',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 12px 35px rgba(255, 107, 107, 0.6)',
                      },
                    }}
                    onClick={handlePlayAsGuest}
                  >
                    <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <PlayArrowIcon sx={{ fontSize: 24, color: 'white' }} />
                        <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>
                          {t("Play as guest")}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Stack>
            </Zoom>
          )}

          {/* Secondary Actions - Learn More as icon only */}
          <Fade in timeout={1500}>
            <Stack 
              direction={isMobile ? "column" : "row"} 
              spacing={2} 
              alignItems="center"
            >
              <IconButton
                onClick={handleLearnMore}
                sx={{
                  color: "white",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  borderRadius: 2,
                  p: 1.5,
                  transition: 'all 0.3s ease',
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    borderColor: "white",
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <Lightbulb sx={{ fontSize: 24 }} />
              </IconButton>

              {!userData?.socialUser && (
                <Button
                  onClick={handleOpenWhyDialog}
                  variant="text"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    textTransform: "none",
                    fontStyle: "italic",
                    fontSize: { xs: "0.8rem", sm: "0.9rem" },
                    textDecoration: 'underline',
                    "&:hover": {
                      color: "white",
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  }}
                >
                  {t("Why sign up?")}
                </Button>
              )}
            </Stack>
          </Fade>
        </Box>
      </Container>

      {/* Enhanced Why Sign Up Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseWhyDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(74, 112, 125, 0.95), rgba(33, 150, 243, 0.95))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 4,
            color: 'white',
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: 'white',
            borderBottom: '1px solid rgba(255,255,255,0.2)',
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {t("Why sign up?")}
          </Typography>
          <IconButton onClick={handleCloseWhyDialog} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            {[
              { icon: <SaveAltIcon />, text: t("Save and search for your games"), color: '#4CAF50' },
              { icon: <GroupAddIcon />, text: t("Invite and play with friends"), color: '#2196F3' },
              { icon: <MonetizationOnIcon />, text: t("Receive daily coins when visiting"), color: '#FFD700' },
              { icon: <ShoppingCartIcon />, text: t("Purchase coins"), color: '#FF9800' },
              { icon: <EmojiEventsIcon />, text: t("Compete in tournaments"), color: '#9C27B0' },
              { icon: <Star />, text: t("Track your progress and rating"), color: '#E91E63' },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Grow in={isDialogOpen} timeout={800 + index * 100}>
                  <Card
                    sx={{
                      background: `linear-gradient(135deg, ${alpha(item.color, 0.2)}, ${alpha(item.color, 0.1)})`,
                      border: `1px solid ${alpha(item.color, 0.3)}`,
                      borderRadius: 2,
                      p: 2,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: `0 8px 25px ${alpha(item.color, 0.3)}`,
                      }
                    }}
                  >
                    <Avatar
                      sx={{
                        background: item.color,
                        mr: 2,
                        width: 40,
                        height: 40,
                      }}
                    >
                      {item.icon}
                    </Avatar>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: 'white',
                        fontSize: { xs: '0.9rem', sm: '1rem' }
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 4 }}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, #4CAF50, #45a049)',
                borderRadius: 3,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: '0 10px 30px rgba(76, 175, 80, 0.4)',
                }
              }}
              onClick={() => {
                handleCloseWhyDialog();
                handleLogin();
              }}
            >
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
                  <KeyIcon sx={{ fontSize: 28, color: 'white' }} />
                  <Typography variant="h6" fontWeight="bold" sx={{ color: 'white' }}>
                    {t("Get Started Now!")}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 1 }}>
                  {t("Join thousands of players worldwide")}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WelcomePage;